/**
 * Analytics and Conversion Tracking
 * Tracks user interactions on the new landing page
 */

import React from 'react';

interface AnalyticsEvent {
  event: string;
  category: string;
  action: string;
  label?: string;
  value?: number;
  userId?: string;
  sessionId?: string;
  timestamp: string;
  page: string;
  userAgent: string;
  properties?: Record<string, any>;
}

interface ConversionEvent {
  type: 'page_view' | 'demo_started' | 'demo_completed' | 'cta_clicked' | 'signup_started' | 'signup_completed' | 'trial_started';
  source: string;
  properties?: Record<string, any>;
}

class Analytics {
  private sessionId: string;
  private userId?: string;
  private isEnabled: boolean;

  constructor() {
    this.sessionId = this.generateSessionId();
    this.isEnabled = !this.isLocalhost();
    this.initializeSession();
  }

  private generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private isLocalhost(): boolean {
    return window.location.hostname === 'localhost' || 
           window.location.hostname === '127.0.0.1' ||
           window.location.hostname.includes('localhost');
  }

  private initializeSession(): void {
    if (!this.isEnabled) return;

    // Track page load
    this.trackEvent({
      event: 'page_load',
      category: 'session',
      action: 'start',
      page: window.location.pathname,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      properties: {
        referrer: document.referrer,
        screenResolution: `${window.screen.width}x${window.screen.height}`,
        viewportSize: `${window.innerWidth}x${window.innerHeight}`,
        language: navigator.language,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
      }
    });
  }

  private async trackEvent(event: AnalyticsEvent): Promise<void> {
    if (!this.isEnabled) {
      console.log('Analytics (dev mode):', event);
      return;
    }

    try {
      // Send to our analytics endpoint
      await fetch('/api/analytics/track', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...event,
          sessionId: this.sessionId,
          userId: this.userId
        })
      });
    } catch (error) {
      console.error('Analytics tracking failed:', error);
    }
  }

  // Public tracking methods
  public trackPageView(page: string, properties?: Record<string, any>): void {
    this.trackEvent({
      event: 'page_view',
      category: 'navigation',
      action: 'view',
      page,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      properties
    });
  }

  public trackConversion(conversion: ConversionEvent): void {
    this.trackEvent({
      event: 'conversion',
      category: 'conversion',
      action: conversion.type,
      label: conversion.source,
      page: window.location.pathname,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      properties: conversion.properties
    });
  }

  public trackCTAClick(ctaText: string, location: string, destination?: string): void {
    this.trackEvent({
      event: 'cta_click',
      category: 'engagement',
      action: 'click',
      label: ctaText,
      page: window.location.pathname,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      properties: {
        location,
        destination,
        ctaText
      }
    });
  }

  public trackDemoStart(demoType: string, trigger: string): void {
    this.trackEvent({
      event: 'demo_start',
      category: 'engagement',
      action: 'start',
      label: demoType,
      page: window.location.pathname,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      properties: {
        demoType,
        trigger
      }
    });
  }

  public trackDemoComplete(demoType: string, duration: number, result?: any): void {
    this.trackEvent({
      event: 'demo_complete',
      category: 'engagement',
      action: 'complete',
      label: demoType,
      value: duration,
      page: window.location.pathname,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      properties: {
        demoType,
        duration,
        result: result ? {
          hasScript: !!result.script,
          hasAudio: !!result.audio,
          hasThumbnail: !!result.thumbnail,
          wordCount: result.script?.length || 0
        } : undefined
      }
    });
  }

  public trackFeatureInteraction(feature: string, action: string, details?: any): void {
    this.trackEvent({
      event: 'feature_interaction',
      category: 'engagement',
      action,
      label: feature,
      page: window.location.pathname,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      properties: {
        feature,
        details
      }
    });
  }

  public trackScrollDepth(percentage: number): void {
    // Only track at 25%, 50%, 75%, 100%
    const milestones = [25, 50, 75, 100];
    if (!milestones.includes(percentage)) return;

    this.trackEvent({
      event: 'scroll_depth',
      category: 'engagement',
      action: 'scroll',
      value: percentage,
      page: window.location.pathname,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      properties: {
        depth: percentage
      }
    });
  }

  public trackTimeOnPage(timeSpent: number): void {
    this.trackEvent({
      event: 'time_on_page',
      category: 'engagement',
      action: 'time_spent',
      value: timeSpent,
      page: window.location.pathname,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      properties: {
        timeSpent
      }
    });
  }

  public setUserId(userId: string): void {
    this.userId = userId;
  }

  public getSessionId(): string {
    return this.sessionId;
  }
}

// Create singleton instance
export const analytics = new Analytics();

// Hook for React components
export const useAnalytics = () => {
  return {
    trackPageView: analytics.trackPageView.bind(analytics),
    trackConversion: analytics.trackConversion.bind(analytics),
    trackCTAClick: analytics.trackCTAClick.bind(analytics),
    trackDemoStart: analytics.trackDemoStart.bind(analytics),
    trackDemoComplete: analytics.trackDemoComplete.bind(analytics),
    trackFeatureInteraction: analytics.trackFeatureInteraction.bind(analytics),
    trackScrollDepth: analytics.trackScrollDepth.bind(analytics),
    trackTimeOnPage: analytics.trackTimeOnPage.bind(analytics),
    setUserId: analytics.setUserId.bind(analytics),
    getSessionId: analytics.getSessionId.bind(analytics)
  };
};

// Scroll tracking hook
export const useScrollTracking = () => {
  React.useEffect(() => {
    let hasTracked25 = false;
    let hasTracked50 = false;
    let hasTracked75 = false;
    let hasTracked100 = false;

    const handleScroll = () => {
      const scrollPercent = Math.round(
        (window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100
      );

      if (scrollPercent >= 25 && !hasTracked25) {
        hasTracked25 = true;
        analytics.trackScrollDepth(25);
      }
      if (scrollPercent >= 50 && !hasTracked50) {
        hasTracked50 = true;
        analytics.trackScrollDepth(50);
      }
      if (scrollPercent >= 75 && !hasTracked75) {
        hasTracked75 = true;
        analytics.trackScrollDepth(75);
      }
      if (scrollPercent >= 100 && !hasTracked100) {
        hasTracked100 = true;
        analytics.trackScrollDepth(100);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
};

// Time on page tracking hook
export const useTimeTracking = () => {
  React.useEffect(() => {
    const startTime = Date.now();

    const trackTimeSpent = () => {
      const timeSpent = Math.round((Date.now() - startTime) / 1000);
      analytics.trackTimeOnPage(timeSpent);
    };

    // Track time when user leaves page
    window.addEventListener('beforeunload', trackTimeSpent);
    
    // Track time periodically for long sessions
    const interval = setInterval(() => {
      const timeSpent = Math.round((Date.now() - startTime) / 1000);
      if (timeSpent > 0 && timeSpent % 60 === 0) { // Every minute
        analytics.trackTimeOnPage(timeSpent);
      }
    }, 60000);

    return () => {
      window.removeEventListener('beforeunload', trackTimeSpent);
      clearInterval(interval);
      trackTimeSpent(); // Final tracking
    };
  }, []);
};

export default analytics;